import { useState } from "react";
import { ArrowUpFromLine, Download, Share } from "@/shared/ui/icons";
import { useTranslation } from "react-i18next";

import { useInstallPrompt } from "@/core/pwa/useInstallPrompt";
import { useLatestRelease, type DetectedOS } from "@/core/pwa/useLatestRelease";
import { useIsMobile } from "@/core/hooks";
import { Button, Infobulle, Ligne, Lignes, Modal, ModalBody, ModalFooter } from "@/shared/ui";

/**
 * Bouton "Install / Telecharger" dans la TitleBar.
 *
 * Logique :
 *   - Dans Tauri (app installee) ou en standalone PWA → masque (rien a faire).
 *   - Sur mobile browser :
 *       - support `native`     → label "Installer l'app", clic = prompt natif Android
 *       - support `ios-manual` → label "Installer l'app", clic = ouvre une modal
 *                                guide "Share > Sur l'ecran d'accueil"
 *       - support `unsupported`→ masque (mobile sans installability, rare)
 *   - Sur desktop browser :
 *       - useLatestRelease() detecte OS + fetch GitHub API pour pinpoint le
 *         binaire idoine (universal.dmg / setup.exe / .AppImage). Clic =
 *         download direct. Si le fetch rate, fallback = page releases.
 */

const OS_LABEL: Record<DetectedOS, string> = {
  windows: "Windows",
  macos: "macOS",
  linux: "Linux",
  unknown: "",
};

export function AppInstallButton({
  variant = "bar",
}: {
  /** "rail" = icône seule, dans le rail gauche du kiosque. */
  variant?: "bar" | "rail";
} = {}) {
  const { t } = useTranslation();
  const { support, promptInstall } = useInstallPrompt();
  const isMobile = useIsMobile();
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  if (support === "in-tauri" || support === "already-installed") {
    return null;
  }

  if (isMobile) {
    if (support === "unsupported") return null;
    return (
      <>
        <Infobulle texte={t("shell.install.installMobileTooltip")}>
          <Button
            variant="secondary"
            size="lg"
            iconOnly
            className="h-9 w-9"
            onClick={() => {
              if (support === "ios-manual") {
                setShowIOSGuide(true);
              } else {
                void promptInstall();
              }
            }}
            aria-label={t("shell.install.installMobile")}
          >
            <ArrowUpFromLine className="h-4 w-4" />
          </Button>
        </Infobulle>
        <IOSInstallGuideModal open={showIOSGuide} onClose={() => setShowIOSGuide(false)} />
      </>
    );
  }

  // Desktop browser : pick the right binary for the OS via GitHub API.
  if (variant === "rail") return <DesktopDownloadRail />;
  return <DesktopDownloadButton />;
}

/** Le téléchargement de l'app de bureau, en icône (rail du kiosque). */
function DesktopDownloadRail() {
  const { t } = useTranslation();
  const { os, directUrl, filename, version } = useLatestRelease();
  const libelle = `${t("shell.install.downloadDesktop")}${OS_LABEL[os] ? ` · ${OS_LABEL[os]}` : ""}`;
  if (!directUrl) return null;
  return (
    <Infobulle texte={`${libelle} — ${filename ?? ""}${version ? ` (${version})` : ""}`} cote="droite">
      <Button
        href={directUrl}
        variant="ghost"
        size="lg"
        iconOnly
        className="h-9 w-9"
        aria-label={libelle}
      >
        <Download className="h-[18px] w-[18px]" />
      </Button>
    </Infobulle>
  );
}

function DesktopDownloadButton() {
  const { t } = useTranslation();
  const { os, directUrl, filename, version } = useLatestRelease();
  const osLabel = OS_LABEL[os];

  // Toujours visible en browser. Si pas de release dispo (repo vide, API
  // down, draft non publiee) → bouton disabled avec tooltip explicatif.
  // Jamais de redirection vers GitHub releases.
  if (!directUrl) {
    return (
      // Un bouton inactif n'émet pas de gestes : l'infobulle se pose sur son enveloppe.
      <Infobulle texte={t("shell.install.downloadDesktopTooltip")}>
        <span className="inline-flex">
          <Button
            variant="secondary"
            size="md"
            disabled
            icon={<Download className="h-3.5 w-3.5" />}
          >
            {t("shell.install.downloadDesktop")}
          </Button>
        </span>
      </Infobulle>
    );
  }

  const tooltip = `${t("shell.install.downloadDesktopTooltip")} — ${filename ?? ""}${
    version ? ` (${version})` : ""
  }`;

  return (
    <Infobulle texte={tooltip}>
      <Button
        href={directUrl}
        variant="secondary"
        size="md"
        icon={<Download className="h-3.5 w-3.5" />}
      >
        {t("shell.install.downloadDesktop")}
        {osLabel && <span className="text-muted">· {osLabel}</span>}
      </Button>
    </Infobulle>
  );
}

/**
 * Modal de tuto pour install iOS Safari. Apple ne fournit aucune API pour
 * declencher l'install par script — l'user doit Share > Add to Home Screen
 * manuellement. On le guide avec 3 etapes + l'icone du Share.
 */
function IOSInstallGuideModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      size="sm"
      title={t("shell.install.ios.title")}
      subtitle={t("shell.install.ios.intro")}
      closeLabel={t("common.close")}
    >
      <ModalBody>
        <Lignes>
          <Ligne
            avant={<span className="text-[15px] font-semibold tabular-nums text-faint">1</span>}
            etiquette={
              <span className="block whitespace-normal leading-snug">
                {t("shell.install.ios.step1")}
                <Share className="ml-1.5 inline h-4 w-4 align-text-bottom text-fg" />
              </span>
            }
          />
          <Ligne
            avant={<span className="text-[15px] font-semibold tabular-nums text-faint">2</span>}
            etiquette={<span className="block whitespace-normal leading-snug">{t("shell.install.ios.step2")}</span>}
          />
          <Ligne
            avant={<span className="text-[15px] font-semibold tabular-nums text-faint">3</span>}
            etiquette={<span className="block whitespace-normal leading-snug">{t("shell.install.ios.step3")}</span>}
          />
        </Lignes>
      </ModalBody>
      <ModalFooter>
        <Button variant="accent" size="lg" onClick={onClose} className="w-full">
          {t("shell.install.ios.done")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
